import { useEffect, useState } from 'react';
import { dungeonService } from '../../services/DungeonService';
import { SPELL_REGISTRY } from '../../data/spells.config';
import { msToTicks } from '../../utils/timing.utils';
import { IconWithKey } from './IconWithKey';
import { DungeonAction } from '../../data/controls.config';
import './SpellBar.scss';

interface SpellSlotState {
  id: string | null;
  name: string;
  icon?: string;
  cooldownPct: number;
}

interface BarState {
  basic: SpellSlotState | null;
  slots: SpellSlotState[];
}

const EMPTY_STATE: BarState = { basic: null, slots: [] };

const SKILL_ACTIONS: DungeonAction[] = [
  'SKILL_1',
  'SKILL_2',
  'SKILL_3',
  'SKILL_4',
];

function buildSlotState(
  id: string | null,
  cooldownPct: number,
): SpellSlotState {
  if (!id) {
    return { id: null, name: '', icon: undefined, cooldownPct: 0 };
  }
  const spell = SPELL_REGISTRY[id];
  return {
    id,
    name: spell?.name ?? id,
    icon: spell?.icon,
    cooldownPct,
  };
}

export const SpellBar = () => {
  const [state, setState] = useState<BarState>(EMPTY_STATE);

  useEffect(() => {
    const tick = () => {
      const player = dungeonService.getPlayer();
      if (!player) {
        setState(EMPTY_STATE);
        return;
      }
      const basicSpell = player.getBasicAttackSpell();
      const basicId = basicSpell.id;
      const basicState = player.getSpellState(basicId);
      const basicTotal = basicSpell.cooldownMs
        ? msToTicks(basicSpell.cooldownMs)
        : 0;
      const basicPct =
        basicTotal > 0
          ? Math.min(1, Math.max(0, basicState.cooldownRemaining) / basicTotal)
          : 0;

      const slots: SpellSlotState[] = [];
      for (let i = 0; i < player.slots.length; i++) {
        const id = player.slots[i].spellId;
        if (!id) {
          slots.push(buildSlotState(null, 0));
          continue;
        }
        const spell = SPELL_REGISTRY[id];
        const cdState = player.getSpellState(id);
        const total = spell?.cooldownMs ? msToTicks(spell.cooldownMs) : 0;
        const pct =
          total > 0
            ? Math.min(1, Math.max(0, cdState.cooldownRemaining) / total)
            : 0;
        slots.push(buildSlotState(id, pct));
      }

      setState({
        // The basic attack can be a trinket-synthesized spell whose id is not in
        // SPELL_REGISTRY — read its name/icon from the resolved spell directly.
        basic: {
          id: basicId,
          name: basicSpell.name,
          icon: basicSpell.icon,
          cooldownPct: basicPct,
        },
        slots,
      });
    };
    tick();
    const interval = setInterval(tick, 100);
    return () => clearInterval(interval);
  }, []);

  if (!state.basic) {
    return null;
  }

  return (
    <div className="spell-bar">
      <IconWithKey
        sprite={state.basic.icon}
        context="dungeon"
        action="BASIC_ATTACK"
        variant="basic"
        title={`${state.basic.name} (attaque de base)`}
      />

      <div className="spell-bar-divider" />

      {state.slots.map((slot, idx) => {
        const action = SKILL_ACTIONS[idx];
        const className = slot.id ? '' : 'icon-with-key--empty';
        return (
          <IconWithKey
            key={action}
            sprite={slot.icon}
            context="dungeon"
            action={action}
            cooldownPct={slot.cooldownPct}
            className={className}
            title={slot.id ? slot.name : 'Slot libre'}
          />
        );
      })}
    </div>
  );
};
